/* eslint-disable jsx-a11y/media-has-caption */
import clsx from 'clsx'
import { format, formatDistanceToNowStrict } from 'date-fns'
import type { FC } from 'react'
import { useState } from 'react'

import DeleteIcon from '@/assets/img/delete-icon.svg?react'
import EditIcon from '@/assets/img/edit-pan.svg?react'
import { getUser } from '@/features/auth/authSlice'
import { useAppSelector } from '@/hooks/redux'
import type { User } from '@/types'
import type { IMessage } from '@/types/chats'
import { getMediaUrl } from '@/utils/helpers'

const renderFiles = (files: IMessage['files']) => {
    return files?.map((el, idx) => {
        const fileUrl = getMediaUrl(el?.fileName)

        const [loading, setLoading] = useState(true)

        const handleLoad = () => {
            setLoading(false)
        }

        if (el.fileType === 'image') {
            return (
                <p
                    key={idx}
                    style={{ paddingBottom: '5px' }}
                >
                    {loading && <p>Loading...</p>}
                    <img
                        style={{ maxWidth: '280px', maxHeight: '280px' }}
                        src={fileUrl}
                        alt={el.fileName}
                        onLoad={handleLoad}
                        onError={handleLoad}
                    />
                </p>
            )
        }

        if (el.fileType === 'video') {
            return (
                <p
                    key={idx}
                    style={{ paddingBottom: '5px' }}
                >
                    {loading && <p>Loading video...</p>}
                    <video
                        style={{ maxWidth: '280px', maxHeight: '280px' }}
                        src={fileUrl}
                        controls
                        onLoadedData={handleLoad}
                        onError={handleLoad}
                    >
                        Your browser does not support the video tag.
                    </video>
                </p>
            )
        }

        if (el.fileType === 'file') {
            return (
                <p
                    key={idx}
                    style={{ paddingBottom: '5px' }}
                >
                    <a
                        href={fileUrl}
                        download
                    >
                        {el.fileName} (Download)
                    </a>
                </p>
            )
        }

        return null
    })
}

interface IProps {
    message: IMessage
    deleteMessage: (id: number) => void
    editMessage: (message: IMessage) => void
}

export const Message: FC<IProps> = ({
    message,
    deleteMessage,
    editMessage,
}) => {
    const currentUser = useAppSelector(getUser)
    if (!message) return null
    const isCurrentUser = message.senderId === currentUser?.id

    const handleRemove = () => {
        deleteMessage(message.id)
    }
    const handleEdit = () => {
        editMessage(message)
    }

    return (
        <div
            className={clsx('chat-message-list-item', {
                '--you-msg': isCurrentUser && !message.isSystem,
                '--system-msg': message.isSystem,
            })}
        >
            <div
                className={`message-block ${isCurrentUser ? 'ms-auto' : 'me-auto'}`}
            >
                <div className="message-body">
                    {!isCurrentUser && !message.isSystem && (
                        <div className="ico-avatar btn btn--sm btn--square">
                            <img
                                src={getMediaUrl(message?.sender?.photo)}
                                alt="avatar"
                            />
                        </div>
                    )}
                    <div className="message-block--content">
                        <div className="message-text">
                            {!!message?.files?.length && (
                                <div>{renderFiles(message?.files)}</div>
                            )}
                            <p>{message?.content}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className={`message-foot ${
                    isCurrentUser ? 'justify-end' : 'justify-start'
                }`}
            >
                <div className="message-foot--item">
                    {isCurrentUser && !message.isSystem && (
                        <div className="message-actions flex">
                            <button
                                onClick={handleEdit}
                                type="button"
                            >
                                <EditIcon />
                            </button>
                            <button
                                onClick={handleRemove}
                                className="btn-drp-link-danger"
                                type="button"
                            >
                                <DeleteIcon />
                            </button>
                        </div>
                    )}
                    <p className="message-name">
                        {message?.isSystem
                            ? 'System'
                            : message?.sender?.fullName}
                    </p>
                    <p className="message-send-time">
                        {message.createdAt
                            ? formatDistanceToNowStrict(
                                  new Date(message?.createdAt),
                              )
                            : ''}
                    </p>
                </div>
            </div>
        </div>
    )
}
